import { Suspense, lazy, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { applyLandingSeo } from "@/lib/seo";
import { ensureFont } from "@/lib/fonts";
import {
  LandingHeader, Hero, ProblemSection, HowItWorks, PersonaSection, ScreensSection, TrustSection, StatsStrip, FaqSection, FinalCta, LandingFooter,
  useCopy, SHOW_EXPLAINER,
} from "@/components/landing/Sections";

// Only fetched when the explainer is switched back on (keeps the video player out of the landing chunk).
const ExplainerSection = lazy(() => import("@/components/landing/Explainer"));

/** Per-language title / description / Open Graph / JSON-LD (committee R-1, R-14) and the Arabic display face (R-10). */
function useLandingDocument() {
  const { lang } = useLang();
  const { c } = useCopy();
  useEffect(() => applyLandingSeo({ lang, title: c.meta.title, description: c.meta.description, faq: c.faq.items }), [lang, c]);
  useEffect(() => { if (lang === "ar") ensureFont("arDisplay"); }, [lang]);
}

/** `/#faq`-style links from other public pages: scroll to the anchor once the page has rendered (the router does not). */
function useHashScroll() {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return undefined;
    const timer = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    return () => clearTimeout(timer);
  }, [hash]);
}

/**
 * Public landing page — nine blocks (Phase 56, committee R-9):
 * Hero (+ proof band) → Problem → How it works → Who it serves → Product screens → Trust & assurance → FAQ → Final CTA → Footer.
 * The live-stats strip renders itself only past the scale thresholds; the calculator lives on its own page (/model).
 */
export default function LandingPage() {
  const { c } = useCopy();
  useLandingDocument();
  useHashScroll();
  return (
    <div className="lp min-h-screen bg-background text-foreground" data-testid="landing-page">
      <a href="#main" className="lp-skip" data-testid="landing-skip-link">{c.skip}</a>
      <LandingHeader />
      <main id="main" tabIndex={-1}>
        <Hero />
        <ProblemSection />
        <HowItWorks />
        <PersonaSection />
        <ScreensSection />
        <TrustSection />
        <StatsStrip />
        <FaqSection />
        {SHOW_EXPLAINER && <Suspense fallback={null}><ExplainerSection /></Suspense>}
        <FinalCta />
      </main>
      <LandingFooter />
    </div>
  );
}
